"use client";

import { useEffect, useState, useCallback } from "react";
import { useQueryState, parseAsStringEnum, parseAsString } from "nuqs";

import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { createSignupSchema, type SignupFormValues } from "@/shared/lib/validation/user-schemas";
import { userRegistration } from "@/server-action/user/user-registration";
import { useRouter } from "next/navigation";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { EmailVerificationWaiting } from "@/shared/ui/email-verification-waiting";
import { SuccessStep } from "@/shared/ui/success-step";
import { SignupStep } from "../model/types";
import { EmailStep } from "./email-step";
import { TermsStep } from "./terms-step";
import { PasswordStep } from "./password-step";
import { ProfileStep } from "./profile-step";
import { useTranslations } from "next-intl";

export function SignupForm() {
  const router = useRouter();
  const t = useTranslations();
  // query state
  const [step, setStep] = useQueryState("step", parseAsStringEnum(Object.values(SignupStep)));
  const [verificationCode, setVerificationCode] = useQueryState("verificationCode", parseAsString);
  const [emailFromUrl, setEmailFromUrl] = useQueryState("email", parseAsString);
  const [errorFromUrl, setErrorFromUrl] = useQueryState("error", parseAsString);
  const schema = createSignupSchema(t);

  // error dialog state
  const [errorDialog, setErrorDialog] = useState<{
    show: boolean;
    title: string;
    description: string;
  }>({
    show: false,
    title: "",
    description: "",
  });

  // 에러 메시지 유틸 함수
  const getErrorMessage = useCallback(
    (errorType: string) => {
      const messages = {
        expired: { title: t("f_com_alert_title_08"), description: t("f_com_alert_desc_08") },
        "already-verified": { title: t("f_com_alert_title_16"), description: t("f_com_alert_desc_16") },
        invalid: { title: "유효하지 않은 코드에요.", description: "인증 메일을 다시 보내주세요." },
      };
      return messages[errorType as keyof typeof messages];
    },
    [t],
  );

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      email: "",
      password: "",
      verificationCode: verificationCode || "",
      countryCode: "BR",
      phoneNumber: "",
      companyName: "",
      zip: "",
      streetAddress: "",
      houseNumber: "",
      area: "",
      city: "",
      state: "",
      complement: "",
      agreeTerms: false,
      agreePrivacy: false,
      agreeMarketing: false,
      adminId: "",
      adminName: "",
    },
  });

  const onClickSendEmail = () => {
    setStep(SignupStep.WAITING);
  };

  const onClickTermsSubmit = () => {
    setStep(SignupStep.PASSWORD);
  };

  const onClickPasswordSubmit = () => {
    setStep(SignupStep.PROFILE);
  };

  // 회원가입 에러 처리 함수
  const handleRegistrationError = (error: unknown) => {
    console.error("❌ Registration failed:", error);

    if (error instanceof Error && error.message === "Email verification expired") {
      setErrorDialog({
        show: true,
        title: t("f_com_alert_title_08"),
        description: t("f_com_alert_desc_08"),
      });
    } else {
      setErrorDialog({
        show: true,
        title: "회원가입 오류",
        description: "회원가입 중 오류가 발생했습니다. 다시 시도해주세요.",
      });
    }
  };

  const onClickProfileSubmit = async () => {
    try {
      const values = form.getValues();
      const safeValues = {
        ...values,
        verificationCode: values.verificationCode as string,
        agreeTerms: values.agreeTerms as boolean,
        agreePrivacy: values.agreePrivacy as boolean,
        agreeMarketing: values.agreeMarketing as boolean,
      };

      // adminName은 화면 표시용이므로 서버 전송에서 제외
      delete safeValues.adminName;

      const result = await userRegistration(safeValues);
      if (!result.ok) {
        throw new Error(result.error.message);
      }
      setStep(SignupStep.SUCCESS);
    } catch (error: unknown) {
      handleRegistrationError(error);
    }
  };

  // 새로고침 시 처음으로 초기화
  useEffect(() => {
    const navigationEntries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    const isRefresh = navigationEntries.length > 0 && navigationEntries[0].type === "reload";

    if (isRefresh) {
      setStep(null);
      setVerificationCode(null);
      setEmailFromUrl(null);
    }
  }, [setStep, setVerificationCode, setEmailFromUrl]);

  // profile 단계에서 비밀번호 없으면 접근 불가
  useEffect(() => {
    if (step === "profile" && !form.getValues("password")) {
      setStep(null);
    }
  }, [step, form, setStep]);

  // verificationCode가 없으면 terms/profile 단계 접근 불가
  useEffect(() => {
    if ((step === "terms" || step === "profile") && !verificationCode) {
      setStep(null);
    }
  }, [step, verificationCode, setStep]);
  // URL에서 받은 이메일로 폼 데이터 업데이트
  useEffect(() => {
    if (emailFromUrl && emailFromUrl !== form.getValues("email")) {
      form.setValue("email", emailFromUrl);
    }
  }, [emailFromUrl, form]);

  // 이메일이 없으면 waiting 단계 접근 불가
  useEffect(() => {
    if (step === "waiting" && !form.getValues("email")) {
      setStep(null);
    }
  }, [step, form, setStep]);

  // 리다이렉트 시 이메일 인증 에러 처리
  useEffect(() => {
    if (errorFromUrl) {
      const errorMessage = getErrorMessage(errorFromUrl);
      if (errorMessage) {
        setErrorDialog({ show: true, ...errorMessage });
        setErrorFromUrl(null);
      }
    }
  }, [errorFromUrl, setErrorFromUrl, getErrorMessage]);

  const renderStep = () => {
    if (!step) return <EmailStep onClickSendEmail={onClickSendEmail} />;
    if (step === "waiting") return <EmailVerificationWaiting email={form.getValues("email")} />;
    if (step === "terms") return <TermsStep onClickTermsSubmit={onClickTermsSubmit} />;
    if (step === "password") return <PasswordStep onSubmit={onClickPasswordSubmit} />;
    if (step === "profile") return <ProfileStep />;
    if (step === "success")
      return (
        <SuccessStep
          title={t("f_jd_title")}
          description={t("f_jd_context")}
          button1={{ label: "f_jd_btn_main", onClick: () => router.push("/") }}
          button2={{ label: "f_jd_btn_login", onClick: () => router.push("/login") }}
        />
      );
  };

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onClickProfileSubmit)}>
          <div
            className={`flex ${step === "profile" ? "h-full" : "pc:h-[calc(100vh-284px)] tb:h-[calc(100vh-406px)] h-[calc(100vh-374px)]"} pc:pt-0 tb:mt-20 pc:mt-[94px] tb:px-10 mt-12 flex-col items-center justify-center bg-white px-4`}
          >
            <div className="flex w-full justify-center overflow-y-auto">{renderStep()}</div>
          </div>
        </form>
      </FormProvider>

      <AlertDialog open={errorDialog.show} onOpenChange={(open) => setErrorDialog((prev) => ({ ...prev, show: open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{errorDialog.title}</AlertDialogTitle>
            <AlertDialogDescription>{errorDialog.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => {
                router.push("/login");
                setErrorDialog({ show: false, title: "", description: "" });
              }}
            >
              {t("f_com_alert_btn_16_01")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
